import { Decimal, RIAL, amount, rialToToman } from './money'
import { groupThousands, toPersianDigits, THOUSANDS_SEP, DECIMAL_SEP } from './persian'

/**
 * Display formatting. Everything the user reads goes through here, so the
 * Persian-digit and separator rules live in one place (design-spec.md §7).
 */

interface AmountOptions {
  /** Fractional digits to keep. Default 0 — Toman is shown whole. */
  decimals?: number
  /** Emit Persian digits. Default true. */
  persian?: boolean
  /** Drop trailing zeros in the fraction. Default true. */
  trim?: boolean
}

function formatDecimal(value: Decimal, options: AmountOptions = {}): string {
  const { decimals = 0, persian = true, trim = true } = options
  let text = value.toFixed(decimals, Decimal.ROUND_DOWN)
  if (trim && decimals > 0 && text.includes('.')) {
    text = text.replace(/\.?0+$/, '')
  }
  const [int = '0', fraction] = text.split('.')
  let out = groupThousands(int, persian ? THOUSANDS_SEP : ',')
  if (fraction) out += (persian ? DECIMAL_SEP : '.') + fraction
  return persian ? toPersianDigits(out) : out
}

/**
 * What a screen prints where the backend serves no value.
 *
 * The market routes return `last_price_irr: null` whenever the dealer's feed is
 * missing or stale, and refuse to substitute a last-known price — so a screen
 * that always has a number to show would be inventing one. This is that
 * absence, made visible.
 */
export const NO_VALUE = '—'

/**
 * A Rial amount from the API, rendered as Toman.
 * `formatToman('20000000')` → `۲٬۰۰۰٬۰۰۰`
 */
export function formatToman(rial: string | Decimal, options?: AmountOptions): string {
  return formatDecimal(rialToToman(rial), options)
}

/** A Rial amount rendered as Rial, for the rare screens that show the raw unit. */
export function formatRial(rial: string | Decimal, options?: AmountOptions): string {
  return formatDecimal(amount(rial), options)
}

/**
 * A crypto amount. Crypto is not Rial, so it is never divided by 10.
 * Defaults to 8 fractional digits with trailing zeros trimmed.
 */
export function formatCrypto(
  value: string | Decimal,
  options: AmountOptions = {},
): string {
  return formatDecimal(amount(value), { decimals: 8, ...options })
}

/* --- amounts in an asset other than Rial ---------------------------------
 *
 * Every §9 body names the asset its figures are denominated in, and §7 now
 * does the same for a market's mark. These render a figure in whatever that
 * asset is — which is the whole difference between «۷۷٬۷۰۴٫۹۵ USDT» and a Rial
 * valuation of the same mark printed as Toman.
 *
 * Shared rather than trade-local: the market list, the asset picker, the price
 * alerts and the dashboard's movers all price a market, and none of them
 * should reach into the trade feature to say so.
 */

/**
 * A quote-unit amount, formatted in its own asset.
 *
 * Rial is the asset with no subunit and the only one with a ÷10 between the
 * wire and the page, so it is the one that reads in Toman and groups in
 * threes. Everything else is a crypto quantity and keeps its decimals — a
 * Tether amount divided by ten is off by an order of magnitude.
 */
export function formatQuoteAmount(value: string | Decimal, quoteAsset: string): string {
  return quoteAsset === RIAL ? formatToman(value) : formatCrypto(value)
}

/** The unit as the user reads it: «تومان» for Rial, the ticker otherwise. */
export function quoteUnitLabel(quoteAsset: string): string {
  return quoteAsset === RIAL ? 'تومان' : quoteAsset
}

/** «۳۰٬۰۰۰٬۰۰۰ تومان» or «۷۷٬۷۰۴٫۹۵ USDT» — the figure and its unit together. */
export function formatInQuote(value: string | Decimal, quoteAsset: string): string {
  return `${formatQuoteAmount(value, quoteAsset)} ${quoteUnitLabel(quoteAsset)}`
}

/**
 * The unit as a **ticker**, for the places that show a symbol rather than a
 * word — the big figure on the confirm sheet and the receipt.
 *
 * `IRT` rather than `IRR` for Rial, because the figure beside it has already
 * been divided by ten: the amount is Toman, and labelling Toman `IRR` is off
 * by an order of magnitude in the one spot the user reads first.
 */
export function quoteTicker(quoteAsset: string): string {
  return quoteAsset === RIAL ? 'IRT' : quoteAsset
}

/**
 * A **price** in a non-Rial quote asset, at a price-like number of places.
 *
 * Two fixed decimals at or above one unit, and never trimmed. `formatCrypto`'s
 * eight-places-trimmed is right for a *quantity* — a BTC balance has to show
 * `0.00006` — and wrong for a price, which is what made `۷۷٬۴۱۴٫۰۰۵` sit on
 * the screen: a five-digit integer part, a three-digit fraction, and the
 * Persian decimal separator «٫» a hair's width from the thousands «٬». Always
 * showing exactly two places gives the eye a fixed rule — the last group is
 * two digits, every other group is three — which is what separates
 * «۷۷٬۴۱۴٫۰۰» from «۷۷٬۴۱۴٬۰۰۵» at a glance.
 *
 * Below one unit it falls back to the trimmed eight, because two places render
 * a 0.00004 USDT coin as «۰» and that is worse than a long number.
 *
 * Deliberately **not** applied to `formatQuoteAmount`, which the trade screens
 * use for `gross`, `net_receive` and a balance. Those are money the user is
 * about to part with or receive, and truncating one to two places would be a
 * wrong figure rather than a rounded one.
 */
function formatQuotePrice(value: string | Decimal, quoteAsset: string): string {
  if (quoteAsset === RIAL) return formatToman(value)
  return amount(value).abs().greaterThanOrEqualTo(1)
    ? formatDecimal(amount(value), { decimals: 2, trim: false })
    : formatCrypto(value)
}

/**
 * A market row's price with its unit, as the coin list and the crypto hub
 * print it.
 *
 * The ticker is appended **only** when the market is quoted in something other
 * than Rial. Those rows draw a bare figure by design — the whole list was
 * Toman, so the unit was context — and appending «تومان» to every one of them
 * to make a Tether row legible would change every screen that shows a price.
 * A `BTCUSDT` row cannot be read without its unit, and a `BTCIRR` row is
 * exactly what it was.
 */
export function formatMarketPrice(
  value: string | Decimal | null | undefined,
  quoteAsset: string | null | undefined,
): string {
  if (value == null || quoteAsset == null) return NO_VALUE
  const figure = formatQuotePrice(value, quoteAsset)
  return quoteAsset === RIAL ? figure : `${figure} ${quoteAsset}`
}

/**
 * The same price with **no unit**, for a list that states the unit once above
 * itself rather than on every row — the asset picker's quote switch.
 */
export function formatMarketPriceBare(
  value: string | Decimal | null | undefined,
  quoteAsset: string | null | undefined,
): string {
  if (value == null || quoteAsset == null) return NO_VALUE
  return formatQuotePrice(value, quoteAsset)
}

/** The bare figure in its own quote unit, for callers that render the unit
 *  themselves — «—» when the feed published nothing. */
export function formatQuoteAmountOr(
  value: string | Decimal | null | undefined,
  quoteAsset: string | null | undefined,
): string {
  if (value == null || quoteAsset == null) return NO_VALUE
  return formatQuoteAmount(value, quoteAsset)
}

/**
 * A market's mark in its own quote unit, or «—» when the feed is missing.
 *
 * `null` is the service refusing to publish a stale price, so it renders as an
 * absence like every other nullable market field — never as a Rial figure
 * standing in for a unit the market is not quoted in.
 */
export function formatInQuoteOr(
  value: string | Decimal | null | undefined,
  quoteAsset: string | null | undefined,
): string {
  if (value == null || quoteAsset == null) return NO_VALUE
  return formatInQuote(value, quoteAsset)
}

/** `'1.3'` → `+۱٫۳٪`, `'-0.42'` → `−۰٫۴۲٪`. */
export function formatPercent(value: string | Decimal, decimals = 2): string {
  const decimal = amount(value)
  const sign = decimal.isNegative() ? '−' : decimal.isZero() ? '' : '+'
  const body = formatDecimal(decimal.abs(), { decimals })
  return `${sign}${body}٪`
}

/**
 * The nullable variants, for the market fields the service may decline to
 * serve.
 *
 * Deliberately separate from `formatToman`/`formatPercent` rather than widening
 * those: a required money field arriving as `null` is a contract failure and
 * should throw where it happens, not quietly render a dash. Only the fields the
 * contract marks nullable are read through these.
 */
export function formatTomanOr(
  rial: string | Decimal | null | undefined,
  options?: AmountOptions,
): string {
  return rial == null ? NO_VALUE : formatToman(rial, options)
}

export function formatPercentOr(
  value: string | Decimal | null | undefined,
  decimals = 2,
): string {
  return value == null ? NO_VALUE : formatPercent(value, decimals)
}

/**
 * The direction a nullable change reads as. `null` when there is no figure —
 * distinct from `false`, which would paint an unknown change red.
 */
export function isUp(changePct: string | Decimal | null | undefined): boolean | null {
  if (changePct == null) return null
  return !amount(changePct).isNegative()
}

/** Persian digits for any already-formatted numeric string. */
export function persianNumber(value: number | string): string {
  return toPersianDigits(String(value))
}

/**
 * `'+989123456789'` → `۰۹۱۲ ۳۴۵ ۶۷۸۹`. Display only; the API always gets E.164.
 */
export function formatPhone(e164: string): string {
  const national = e164.replace(/^\+98/, '0')
  const grouped = national.replace(/^(\d{4})(\d{3})(\d{4})$/, '$1 $2 $3')
  return toPersianDigits(grouped)
}

/**
 * `'6037991234567890'` → `۶۰۳۷ - ۹۹۱۲ - ۳۴۵۶ - ۷۸۹۰`: a card number in the
 * four groups the design draws. The groups read left to right, so render the
 * result LTR — `sx-ltr`, or an isolate in plain text.
 *
 * `separator` is for a line of running text, such as the KYC check line,
 * which spaces the groups rather than dashing them.
 */
export function formatCardNumber(cardNumber: string, separator = ' - '): string {
  const digits = cardNumber.replace(/\D/g, '')
  return toPersianDigits((digits.match(/.{1,4}/g) ?? []).join(separator))
}

/**
 * `'IR120120000000203456789001'` → `IR۱۲ ۰۱۲۰ ۰۰۰۰ …`: an IBAN in the groups
 * of four the deposit frame (`202:1674`) draws. Left to right, like a card.
 */
export function formatIban(iban: string): string {
  const compact = iban.replace(/\s/g, '').toUpperCase()
  return toPersianDigits((compact.match(/.{1,4}/g) ?? []).join(' '))
}

/** `'0912345****'` from the API is already masked; just Persian-ise it. */
export function formatMaskedPhone(masked: string): string {
  return toPersianDigits(masked)
}

/** Seconds → `۰۱:۴۵`, for the OTP resend countdown. */
export function formatCountdown(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds))
  const minutes = String(Math.floor(safe / 60)).padStart(2, '0')
  const seconds = String(safe % 60).padStart(2, '0')
  return toPersianDigits(`${minutes}:${seconds}`)
}

/** `'TQrY8mkP…x2Wd9AzK'` — wallet addresses are elided in the middle (§7). */
export function truncateMiddle(value: string, head = 8, tail = 8): string {
  if (value.length <= head + tail + 1) return value
  return `${value.slice(0, head)}…${value.slice(-tail)}`
}

/**
 * `۱۰ دقیقه پیش`, `دیروز`, `۱ ساعت پیش` — the relative stamps on the
 * dashboard's recent-transactions card. Anything older than a week falls back
 * to the Jalali date, which the caller formats.
 */
export function formatRelativeTime(value: string | Date, now: Date = new Date()): string | null {
  const date = value instanceof Date ? value : new Date(value)
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000)
  if (Number.isNaN(seconds)) return null
  if (seconds < 60) return 'همین حالا'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${toPersianDigits(String(minutes))} دقیقه پیش`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${toPersianDigits(String(hours))} ساعت پیش`
  const days = Math.floor(hours / 24)
  if (days === 1) return 'دیروز'
  if (days < 7) return `${toPersianDigits(String(days))} روز پیش`
  return null
}
